import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFlags, useLabels, usePrefs } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import VehicleLink from './VehicleLink';
import { addImageParams } from '../../../utilities/images';

const VehicleImage = ({
	image,
	placeholderHeight,
	link,
	uuid,
	enableImage,
	onImageLoad,
	// Named `linkTabIndex` (not `tabIndex`) because react-slick clones each
	// direct slide child and forces its own `tabIndex="-1"` on it — a prop
	// named `tabIndex` here would be overwritten before reaching the anchor.
	linkTabIndex
}) => {
	const [isInitialLoad, setIsInitialLoad] = useState(false);
	const [placeholderStyles, setPlaceholderStyles] = useState(null);
	const { titleAttributeBefore, titleAttributeAfter } = usePrefs();
	const flags = useFlags();
	const labels = useLabels();
	const imageRef = useRef();
	const {
		uri = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=',
		title = ''
	} = image || {};
	const isFixHeightImage = flags['srp-image-fix-height'];

	const onLoad = () => {
		if (imageRef.current.src) {
			setIsInitialLoad(true);
			onImageLoad(imageRef);
		}
	};
	let src = uri;
	if (image) {
		// don't add params to blank.gif
		src = addImageParams(uri);
	}

	useEffect(() => {
		if (isInitialLoad) {
			setPlaceholderStyles(null);
		} else if (placeholderHeight) {
			setPlaceholderStyles({
				height: placeholderHeight,
				overflow: 'hidden',
				padding: 0
			});
		}
	}, [isInitialLoad, placeholderHeight]);

	if (!enableImage) {
		return null;
	}

	const imageTitle = setClassNames([
		labels.get(titleAttributeBefore),
		title,
		labels.get(titleAttributeAfter)
	]);

	return (
		<VehicleLink link={link} uuid={uuid} tabIndex={linkTabIndex}>
			<div
				className={setClassNames([
					'img-container',
					'img-placeholder',
					'img-loaded', // todo Do we want the opacity transition here?
					isFixHeightImage ? 'fix-height' : ''
				])}
				style={placeholderStyles}
			>
				<img
					loading="eager"
					// eslint-disable-next-line react/no-unknown-property
					fetchpriority="high"
					alt={imageTitle}
					ref={imageRef}
					title={imageTitle}
					onLoad={onLoad}
					onError={onLoad}
					className="w-100"
					src={src}
					style={{
						aspectRatio: uri.startsWith('data:')
							? '4 / 3'
							: 'auto 4 / 3'
					}}
				/>
			</div>
		</VehicleLink>
	);
};

VehicleImage.propTypes = {
	image: PropTypes.shape({
		uri: PropTypes.string,
		title: PropTypes.string
	}),
	link: PropTypes.string,
	uuid: PropTypes.string.isRequired,
	enableImage: PropTypes.bool,
	onImageLoad: PropTypes.func,
	placeholderHeight: PropTypes.number,
	linkTabIndex: PropTypes.number
};

VehicleImage.defaultProps = {
	link: '',
	enableImage: true,
	onImageLoad: () => {},
	placeholderHeight: null,
	linkTabIndex: null
};

export default VehicleImage;
